<template>
  <b-card class="mb-2 analytics-table-card">
    <template #header>
      <div class="section-header">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="FileTextIcon"
            size="16"
            class="mr-75 text-primary"
          />
          <h6 class="card-title mb-0">
            Orders
          </h6>
        </div>
        <span class="badge-count">{{ orders.length }} total</span>
        <download-menu
          filename="orders"
          :rows="orders"
          :columns="downloadColumns"
        />
      </div>
    </template>

    <b-table
      :items="orders"
      :fields="fields"
      sticky-header="420px"
      small
      hover
      show-empty
      empty-text="No orders found for the selected period"
      class="analytics-table mb-0"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template #cell(date)="{ item }">
        <span class="text-nowrap">{{ formatDate(item.date) }}</span>
      </template>

      <template #cell(orderSeqId)="{ item }">
        <span class="font-weight-bold text-primary">{{ item.orderSeqId }}</span>
      </template>

      <template #cell(status)="{ item }">
        <b-badge
          pill
          :class="getStatusClass(item.status)"
          class="text-capitalize status-badge"
        >
          {{ item.status }}
        </b-badge>
      </template>

      <template #cell(quantity)="{ item }">
        <span class="font-weight-semibold">{{ Number(item.quantity || 0).toLocaleString() }}</span>
      </template>

      <template #cell(productCount)="{ item }">
        <span class="font-weight-semibold">{{ Number(item.productCount || 0).toLocaleString() }}</span>
      </template>

      <template #cell(action)="{ item }">
        <b-link
          :to="{ name: 'order/detail', params: { orderId: item.orderId } }"
          class="view-link text-nowrap"
        >
          View order
        </b-link>
      </template>

      <template #cell(totalSales)="{ item }">
        <span class="font-weight-semibold text-nowrap">
          {{ formatCurrency(item.totalSales || 0) }}
        </span>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import {
 BCard, BTable, BBadge, BLink,
} from 'bootstrap-vue'
import moment from 'moment'
import DownloadMenu from './DownloadMenu.vue'
import { formatCurrency } from '../analyticsFormat'

export default {
  name: 'AnalyticsOrdersTable',
  components: {
 BCard, BTable, BBadge, BLink, DownloadMenu,
},
  props: {
    orders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sortBy: 'date',
      sortDesc: true,
      fields: [
        {
 key: 'date', label: 'Date', sortable: true, thClass: 'table-col-header',
},
        {
 key: 'orderSeqId', label: 'Order ID', sortable: false, thClass: 'table-col-header',
},
        {
 key: 'status', label: 'Status', sortable: true, thClass: 'table-col-header',
},
        {
 key: 'retailerName', label: 'Retailer', sortable: false, thClass: 'table-col-header',
},
        {
 key: 'collectionName', label: 'Collection', sortable: false, thClass: 'table-col-header',
},
        {
 key: 'quantity', label: 'Quantity', sortable: true, thClass: 'table-col-header text-right', tdClass: 'text-right',
},
        {
 key: 'productCount', label: 'Products', sortable: true, thClass: 'table-col-header text-right', tdClass: 'text-right',
},
        {
 key: 'action', label: 'Actions', sortable: false, thClass: 'table-col-header',
},
        {
 key: 'totalSales', label: 'Total', sortable: true, thClass: 'table-col-header text-right', tdClass: 'text-right',
},
      ],
    }
  },
  computed: {
    downloadColumns() {
      const fmt = n => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)
      return [
        { key: 'date', label: 'Date', format: v => moment(v).format('MMM DD, YYYY') },
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'status', label: 'Status' },
        { key: 'retailerName', label: 'Retailer' },
        { key: 'collectionName', label: 'Collection' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'productCount', label: 'Products' },
        { key: 'totalSales', label: 'Total', format: v => fmt(v) },
      ]
    },
  },
  methods: {
    formatCurrency,
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY')
    },
    getStatusClass(status) {
      const map = {
        confirmed: 'status-confirmed',
        pending: 'status-pending',
        cancelled: 'status-cancelled',
        rejected: 'status-rejected',
        modified: 'status-modified',
        shipped: 'status-confirmed',
        delivered: 'status-confirmed',
      }
      return map[String(status).toLowerCase()] || 'status-pending'
    },
  },
}
</script>

<style lang="scss" scoped>
@import './analytics-table';

.view-link {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e40af;
  &:hover { color: #1d4ed8; text-decoration: underline; }
}

.status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;

  &.status-confirmed { background: #dcfce7; color: #15803d; }
  &.status-pending   { background: #fef3c7; color: #d97706; }
  &.status-cancelled { background: #fee2e2; color: #dc2626; }
  &.status-rejected  { background: #fee2e2; color: #dc2626; }
  &.status-modified  { background: #e0e7ff; color: #4338ca; }
}
</style>
